<template>
  <div>
    <FilterView :query='query' :searchKeys='collection.getSearchKeys()' :filters='collection.getIndexFilters()' />
    
    <template v-if='!query.isLoading()'>
      <ErrorNotice v-if='query.hasError()' :error='query.getError()' class='mt-4' />
      <WarningNotice v-if='!query.isEmpty() && !query.hasItems()' class='mt-4'>
        <template #title>No {{ collection.getPluralLabel(true) }} found.</template>
      </WarningNotice>
      <EmptyView v-if='query.isEmpty()' class='mt-4'>
        <template #title>No {{ collection.getPluralLabel(true) }}</template>
        <template #message>Get started by creating a new {{ collection.getSingleLabel(true) }}.</template>
      </EmptyView>
    </template>
    
    <LoadingLayout :isLoading='query.isLoading()' class='mt-4' >
      <TableView :query='query' :columns='collection.getIndexColumns()' :bulkActions='bulkActions' />
    </LoadingLayout>
    
    <PaginationView :query='query' class='mt-4' v-if='query.hasItems()' />
    
    <ErrorAlert :controller='errorAlertController' />
    
    <ConfirmAlert :controller='confirmAlertController'>
      <template #title>
        Delete {{ collection.getPluralLabel() }}
      </template>
      <template #message>
        Are you sure you want to delete the selected {{ collection.getPluralLabel(true) }}? This action cannot be undone.
      </template>
      <template #confirm>
        Delete {{ collection.getPluralLabel() }}
      </template>
    </ConfirmAlert>
    
    <ProgressAlert :controller='progressAlertController'>
      <template #title>
        Deleting {{ collection.getPluralLabel() }}
      </template>
    </ProgressAlert>
  </div>
</template>

<script setup>

import { onMounted } from 'vue';

import { usePayloadQuery, doPayloadQuery } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';

import { useAlertController } from '@/kernel/controllers/alert-controller';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ErrorAlert from '@/components/alerts/error-alert.vue';
import ConfirmAlert from '@/components/alerts/confirm-alert.vue';
import ProgressAlert from '@/components/alerts/progress-alert.vue';

import FilterView from '@/components/views/filter-view.vue';
import TableView from '@/components/views/table-view.vue';
import PaginationView from '@/components/views/pagination-view.vue';
import EmptyView from '@/components/views/empty-view.vue';

import ErrorNotice from '@/components/notices/error-notice.vue';
import WarningNotice from '@/components/notices/warning-notice.vue';

const props = defineProps({
  collection: String
});

const collection = getCollection(props.collection);

const errorAlertController = useAlertController();
const confirmAlertController = useAlertController();
const progressAlertController = useAlertController();

const query = usePayloadQuery(collection.getApiIndexEndpoint(), {
  sort: collection.getDefaultSort(),
  contexts: collection.getIndexContextFilters(),
  selects: collection.getIndexSelects()
});

const bulkActions = [
  {
    title: 'Delete selected',
    processor: async () => {
      const { result } = await confirmAlertController.present();
      
      if (result !== 'confirm') {
        return;
      }
      
      progressAlertController.present();
      
      try {
        await doPayloadQuery('DELETE', collection.getApiIndexEndpoint(), {
          params: {
            filters: {
              id: {
                in: query.getSelected().map(item => collection.getId(item))
              }
            }
          }
        });
        
        query.refresh();
        
        progressAlertController.dismiss();
      } catch(e) {
        progressAlertController.dismiss();
        
        errorAlertController.present({
          data: {error: e}
        });
      }
    }
  }
];

onMounted(() => {
  query.refresh();
});

</script>